import { useTranslation } from '@nobia/zeus-components/lib/i18n/useTranslation'
import styled, {
  createGlobalStyle,
  theme,
} from '@nobia/zeus-components/lib/styled'
import rem from '@nobia/zeus-components/lib/styled/rem'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { useRoutes } from '../../routes'
import CloseUi from '../close/Close'
import { Image } from '../image/Image'
import { LinkSolidButton } from '../link/ButtonLink'

const GlobalStyle = createGlobalStyle`
  body {
      height: auto;
      overflow-y: default;
      padding-right: 0;
    }
  ${(p) =>
    p.visible &&
    `
    body {
      height: 100vh;
      overflow-y: hidden;
    }
  `}
`

const Wrapper = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  transform: ${(p) => (p.isOpen ? `translateX(0)` : `translateX(100vw)`)};
  transition: transform 200ms ease-in-out;
  background-color: #fff;
  z-index: 1000;
  border-radius: ${rem(2)};
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  ${(p) => p.theme.media.min.lg`
    max-width: ${rem(600)};
    transform: ${p.isOpen ? 'translateX(0)' : `translateX(${rem(600)})`};
  `}
  ${(p) => p.theme.media.min.xl`
    max-width: ${rem(750)};
    transform: ${p.isOpen ? 'translateX(0)' : `translateX(${rem(750)})`};
  `}
`

const CloseButton = styled(CloseUi)`
  position: absolute;
  top: ${rem(15)};
  right: ${rem(15)};
  ${(p) => p.theme.media.min.md`
    top: ${rem(20)}
    right: ${rem(15)};
  `}
`

const Content = styled.div`
  height: 100%;
  padding: ${rem(20)} ${rem(20)} ${rem(40)} ${rem(20)};
  overflow-x: hidden;
  overflow-y: ${(p) => (!p.isOpen ? 'hidden' : 'auto')};
`

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #000;
  opacity: ${(p) => (p.isOpen ? 0.5 : 0)};
  transition: opacity 200ms ease-in-out;
`

const H1 = styled.h1`
  margin: ${rem(30)} ${rem(15)} ${rem(30)};
  text-align: left;
  font-size: ${rem(36)};
  font-weight: ${theme('typography.heading1.fontWeight')};
  font-style: normal;
  font-family: ${theme('typography.heading1.fontFamily')};
  letter-spacing: normal;
  line-height: 37px;
  text-align: left;
  ${(p) => p.theme.media.min.md`
    margin-left: ${rem(35)};
    margin-bottom: ${rem(30)};
  `};
`

const H3 = styled.h3`
  display: block;
  font-size: ${rem(14)};
  font-style: normal;
  font-family: ${(p) => p.theme.typography.heading3.fontFamily};
  letter-spacing: normal;
  line-height: ${rem(19)};
  text-align: left;
  font-weight: 700;
`

const P = styled.p`
  margin: 0 0 ${rem(20)} 0;
  font-size: ${rem(14)};
  font-weight: ${(p) => p.theme.typography.paragraph.fontWeight};
  font-style: normal;
  letter-spacing: normal;
  line-height: ${rem(19)};
  text-align: left;
`

const ButtonWrapper = styled.div`
  max-width: ${rem(300)};
`

const Section = styled.div``

export const CampaignModal = ({ visible, onClose, image, deals }) => {
  const { t } = useTranslation('app.components.campaign.modal')
  const routes = useRoutes()
  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (visible) {
      setIsVisible(true)
      setTimeout(() => {
        setIsOpen(true)
      }, 100)
    } else {
      setIsOpen(false)
      setTimeout(() => {
        setIsVisible(false)
      }, 300)
    }
  }, [visible])

  return isVisible ? (
    <>
      <GlobalStyle visible />
      <Backdrop onClick={onClose} isOpen={isOpen} />
      <Wrapper isOpen={isOpen}>
        <CloseButton onClick={onClose} />
        <H1>{t('title')}</H1>
        <Content isOpen={isOpen}>
          <Image publicId={image} height={300} crop="fill" />
          {deals?.map(({ title, description }) => (
            <Section key={`deal-${title}`}>
              <H3>{title}</H3>
              <P>{description}</P>
            </Section>
          )) || null}
          <ButtonWrapper>
            <LinkSolidButton
              to={`${routes.appointment.path}book-a-consultation/`}
            >
              {t('link.label')}
            </LinkSolidButton>
          </ButtonWrapper>
        </Content>
      </Wrapper>
    </>
  ) : null
}

CampaignModal.defaultProps = {
  image: null,
  deals: [],
}

CampaignModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  image: PropTypes.string,
  deals: PropTypes.arrayOf(
    PropTypes.shape({ title: PropTypes.string, description: PropTypes.string })
  ),
}
